'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import ImageComponent from '@/src/components/common/ImageComponent'
import { ArrowUpRightSVG, ChevronDownSVG, CloseIconSVG } from '@/src/ui/AllSVG'
import { getBlogsPath, getCategoriesPath, getHomePath, getShopCategoryPath, getSingleProductPath } from '@/src/routes/routes'
import {
    ChildrenCategoriesEntity,
    IMegaMenuCategoriesAPIResponseData,
    IMostWantedProductsEntity,
} from '@/src/types/megaMenu'
interface MobileNavOffcanvasProps {
    megaMenuData: IMegaMenuCategoriesAPIResponseData[]
    /** Resolved logo URL (global API or fallback); passed from PrimaryHeader */
    logoSrc: string
    isOpen: boolean
    onClose: () => void
}

export default function MobileNavOffcanvas({ megaMenuData, logoSrc, isOpen, onClose }: MobileNavOffcanvasProps) {
    const [selectedCategory, setSelectedCategory] = useState<IMegaMenuCategoriesAPIResponseData | null>(null)
    const base = getCategoriesPath()

    const selectedSlug = selectedCategory?.slug ?? ''

    const HIGHLIGHTS = useMemo(
        () => [
            {
                label: 'Exclusive ',
                italic: 'Products',
                href: `${base}?category=${encodeURIComponent(selectedSlug)}&is_exclusive=true`,
            },
            {
                label: 'Under 499',
                italic: '',
                href: `${base}?category=${encodeURIComponent(selectedSlug)}&priceMin=0&priceMax=500`,
            },
            {
                label: 'Most Wanted',
                italic: '',
                href: `${base}?category=${encodeURIComponent(selectedSlug)}&sort=best_seller`,
            },
            {
                label: 'New  ',
                italic: 'Arrivals',
                href: `${base}?category=${encodeURIComponent(selectedSlug)}&sort=new_arrival`,
            },
        ],
        [selectedSlug],
    )

    const { categoriesLinks, featuredProducts } = useMemo(() => {
        if (!selectedCategory) {
            return {
                categoriesLinks: [] as { label: string; href: string }[],
                featuredProducts: [] as IMostWantedProductsEntity[],
            }
        }
        const children = (selectedCategory.children_categories ?? []).filter(
            (c): c is ChildrenCategoriesEntity => c != null,
        )
        const viewAll = {
            label: 'View All Categories',
            href: getShopCategoryPath(selectedCategory.slug),
        }
        const childLinks =
            children.length > 0
                ? children.map((child) => ({
                      label: child.name,
                      href: getShopCategoryPath(selectedCategory.slug, child.slug),
                  }))
                : []
        const featuredRaw =
            selectedCategory.most_wanted_products?.length &&
            selectedCategory.most_wanted_products.length > 0
                ? selectedCategory.most_wanted_products.slice(0, 2)
                : []
        const featuredProducts = (
            featuredRaw.filter((p): p is IMostWantedProductsEntity => p != null)
        )
        return {
            categoriesLinks: [viewAll, ...childLinks],
            featuredProducts,
        }
    }, [selectedCategory, base])

    const brandsLinks = useMemo(() => {
        const brands = (selectedCategory?.brands ?? []).filter((b): b is NonNullable<typeof b> => b != null)
        return [
            { label: 'View All Brands', href: `${base}?category=${encodeURIComponent(selectedSlug)}` },
            ...brands.map((brand) => ({
                label: brand.name,
                href: `${base}?category=${encodeURIComponent(selectedSlug)}&brand=${encodeURIComponent(brand.slug)}`,
            })),
        ]
    }, [selectedCategory, selectedSlug, base])

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
            setSelectedCategory(null)
        }
        return () => {
            document.body.style.overflow = ''
        }
    }, [isOpen])

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            if (selectedCategory) setSelectedCategory(null)
            else onClose()
        },
        [selectedCategory, onClose],
    )

    useEffect(() => {
        if (!isOpen) return
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, handleKeyDown])

    const handleNavClick = () => {
        onClose()
        setSelectedCategory(null)
    }

    const headerIconBtnClassName =
        'inline-flex shrink-0 items-center justify-center size-8 rounded-full bg-secondary-background-color text-black hover:opacity-70 active:scale-95 transition-all cursor-pointer'

    return (
        <div
            className={`lg:hidden fixed inset-0 z-100 transition-[visibility] duration-300 ${isOpen ? 'visible pointer-events-auto' : 'invisible pointer-events-none'}`}
            aria-hidden={!isOpen}
        >
            <button
                type="button"
                aria-label="Close menu"
                className={`absolute inset-0 bg-black/50 transition-opacity duration-300 cursor-pointer ${isOpen ? 'opacity-100' : 'opacity-0'}`}
                onClick={onClose}
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-label={selectedCategory ? selectedCategory.name : 'Navigation menu'}
                className={`absolute top-0 right-0 flex h-full w-full max-w-[min(100vw,400px)] flex-col bg-white shadow-[0_8px_40px_0_rgba(0,0,0,0.12)] transition-transform duration-300 ease-out border-l border-muted-color ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {selectedCategory ? (
                    <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-muted-color px-4 py-3 min-h-[56px]">
                        <button
                            type="button"
                            onClick={() => setSelectedCategory(null)}
                            className={`${headerIconBtnClassName} justify-self-start`}
                            aria-label="Back to menu"
                        >
                            <ChevronDownSVG className="w-4 h-4 rotate-90 shrink-0" />
                        </button>
                        <span className="max-w-36 truncate text-center font-medium uppercase tracking-widest text-black">
                            {selectedCategory.name}
                        </span>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`${headerIconBtnClassName} justify-self-end`}
                            aria-label="Close menu"
                        >
                            <CloseIconSVG />
                        </button>
                    </div>
                ) : (
                    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-muted-color px-4 py-3 min-h-[56px]">
                        <Link
                            href={getHomePath()}
                            onClick={onClose}
                            className="shrink-0"
                        >
                            <div className="w-24 h-8 invert">
                                <ImageComponent src={logoSrc} alt="Logo" object_cover={false} priority={false} />
                            </div>
                        </Link>
                        <button
                            type="button"
                            onClick={onClose}
                            className={headerIconBtnClassName}
                            aria-label="Close menu"
                        >
                            <CloseIconSVG />
                        </button>
                    </div>
                )}

                <nav className="min-h-0 flex-1 overflow-y-auto px-4 py-5">
                    {!selectedCategory ? (
                        <ul className="flex flex-col">
                            {megaMenuData.map((cat) => (
                                <li key={cat._id} className="border-b border-muted-color last:border-b-0">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedCategory(cat)}
                                        className="flex w-full items-center justify-between py-4 text-left text-xs font-medium uppercase tracking-widest text-black hover:opacity-70 transition-opacity cursor-pointer"
                                    >
                                        {cat.name}
                                        <ChevronDownSVG className="w-3 h-3 -rotate-90 shrink-0 text-black" />
                                    </button>
                                </li>
                            ))}
                            <li className="border-b border-muted-color last:border-b-0">
                                <Link
                                    href={getBlogsPath()}
                                    onClick={handleNavClick}
                                    className="flex w-full items-center py-4 text-xs font-medium uppercase tracking-widest text-black hover:opacity-70 transition-opacity"
                                >
                                    Stories
                                </Link>
                            </li>
                        </ul>
                    ) : (
                        <div className="flex flex-col gap-10 pb-8">
                            <div className="space-y-4">
                                <h3 className="font-medium border-b border-muted-color pb-3 text-black">Highlights</h3>
                                <ul className="space-y-3">
                                    {HIGHLIGHTS.map((item, index) => (
                                        <li key={`${item.href}-${index}`}>
                                            <Link
                                                href={item.href}
                                                onClick={handleNavClick}
                                                className="inline-block text-lg font-semibold capitalize text-black hover:opacity-50 transition-opacity duration-200"
                                            >
                                                {item.label}
                                                {item.italic && (
                                                    <em className="font-secondary italic">{item.italic}</em>
                                                )}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-medium border-b border-muted-color pb-3 text-black">Categories</h3>
                                <ul className="space-y-3">
                                    {categoriesLinks.map((item, index) => (
                                        <li key={`${item.href}-${index}`}>
                                            <Link
                                                href={item.href}
                                                onClick={handleNavClick}
                                                className="block capitalize text-black hover:opacity-50 transition-opacity duration-200"
                                            >
                                                {item.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="space-y-4">
                                <h3 className="font-medium border-b border-muted-color pb-3 text-black">Brands</h3>
                                <ul className="space-y-3">
                                    {brandsLinks.map((item, index) => (
                                        <li key={`${item.href}-${index}`}>
                                            <Link
                                                href={item.href}
                                                onClick={handleNavClick}
                                                className="block capitalize text-black hover:opacity-50 transition-opacity duration-200"
                                            >
                                                {item.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {featuredProducts.length > 0 && (
                                <div className="space-y-4">
                                    <h3 className="font-medium border-b border-muted-color pb-3 text-black">
                                        Featured
                                    </h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        {featuredProducts.map((product) => (
                                            <Link
                                                key={product._id}
                                                href={getSingleProductPath(product.slug ?? '')}
                                                onClick={handleNavClick}
                                                className="group block space-y-2.5"
                                            >
                                                <div className="relative aspect-3/4 w-full bg-secondary-background-color rounded-xl overflow-hidden">
                                                    <ImageComponent
                                                        src={
                                                            product.variants?.[0]?.images?.[0] ||
                                                            product.thumbnail
                                                        }
                                                        alt={product.title}
                                                        object_cover={false}
                                                        priority={false}
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <p className="text-xs font-medium truncate text-black">
                                                        {product.title}
                                                    </p>
                                                    <p className="text-[10px] tracking-[0.14px] uppercase flex items-center gap-1 underline underline-offset-2 text-black group-hover:opacity-60 transition-opacity duration-200">
                                                        Explore Now
                                                        <ArrowUpRightSVG className="text-body-color" />
                                                    </p>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </nav>
            </div>
        </div>
    )
}
